import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

interface Task {
  id: string;
  content: string;
  created_at: string;
  action_status: 'pending' | 'completed';
  conversation_id: string | null;
  conversations?: {
    customer_id: string | null;
    customers?: {
      first_name: string | null;
      last_name: string | null;
    } | null;
    messages?: {
      channel: string;
    }[];
  } | null;
}

export function useTasks(
  tenantId: string | null,
  statusFilter?: 'all' | 'pending' | 'completed',
  conversationIds?: string[]
) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Fetch tasks with optional filters
  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['tasks', tenantId, statusFilter, conversationIds],
    queryFn: async () => {
      if (!tenantId) return [];

      let query = supabase
        .from('notes')
        .select(`
          id,
          content,
          created_at,
          action_status,
          conversation_id,
          conversations (
            customer_id,
            customers (
              first_name,
              last_name
            ),
            messages (
              channel
            )
          )
        `)
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });

      if (statusFilter && statusFilter !== 'all') {
        query = query.eq('action_status', statusFilter);
      }

      if (conversationIds && conversationIds.length > 0) {
        query = query.in('conversation_id', conversationIds);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as Task[];
    },
    enabled: !!tenantId && (!conversationIds || conversationIds.length > 0),
  });

  // Fetch pending tasks count
  const { data: pendingTasksCount = 0 } = useQuery({
    queryKey: ['pending-tasks-count', tenantId],
    queryFn: async () => {
      if (!tenantId) return 0;

      const { count, error } = await supabase
        .from('notes')
        .select('*', { count: 'exact', head: true })
        .eq('tenant_id', tenantId)
        .eq('action_status', 'pending');

      if (error) throw error;
      return count || 0;
    },
    enabled: !!tenantId,
  });

  // Subscribe to real-time updates for notes
  useEffect(() => {
    if (!tenantId) return undefined;

    const notesChannel = supabase
      .channel(`tenant:${tenantId}:notes`, {
        config: { private: true }
      })
      .on('broadcast', { event: '*' }, () => {
        // Invalidate all task-related queries
        queryClient.invalidateQueries({ queryKey: ['tasks', tenantId] });
        queryClient.invalidateQueries({ queryKey: ['pending-tasks-count', tenantId] });
        queryClient.invalidateQueries({ queryKey: ['pending-notes', tenantId] });
      });

    const setupChannel = async () => {
      await supabase.realtime.setAuth();
      await notesChannel.subscribe();
    };

    setupChannel();

    return () => {
      supabase.removeChannel(notesChannel);
    };
  }, [tenantId, queryClient]);

  // Complete task mutation
  const completeTaskMutation = useMutation({
    mutationFn: async (taskId: string) => {
      const { error } = await supabase
        .from('notes')
        .update({ action_status: 'completed' })
        .eq('id', taskId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t('taskCompleted'));
      queryClient.invalidateQueries({ queryKey: ['tasks', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['pending-tasks-count', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['pending-notes', tenantId] });
    },
    onError: () => {
      toast.error(t('failedToCompleteTask'));
    },
  });

  // Reopen task mutation
  const reopenTaskMutation = useMutation({
    mutationFn: async (taskId: string) => {
      const { error } = await supabase
        .from('notes')
        .update({ action_status: 'pending' })
        .eq('id', taskId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t('taskReopened'));
      queryClient.invalidateQueries({ queryKey: ['tasks', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['pending-tasks-count', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['pending-notes', tenantId] });
    },
    onError: () => {
      toast.error(t('failedToReopenTask'));
    },
  });

  return {
    tasks,
    isLoading,
    pendingTasksCount,
    completeTask: completeTaskMutation.mutate,
    isCompletingTask: completeTaskMutation.isPending,
    reopenTask: reopenTaskMutation.mutate,
    isReopeningTask: reopenTaskMutation.isPending,
  };
}
